import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Upload, Trash2, Image, CheckCircle, AlertCircle, Copy, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'hero', name: 'Hero Slideshow', description: 'Homepage hero section images (1920x1080 recommended)' },
  { id: 'gallery', name: 'Gallery', description: 'Portfolio and project images (800x600 recommended)' },
  { id: 'services', name: 'Services', description: 'Service section images (800x600 recommended)' },
  { id: 'events', name: 'Events', description: 'Event page images (1200x800 recommended)' },
  { id: 'team', name: 'Team', description: 'Team member photos (400x400 recommended)' },
  { id: 'clients', name: 'Clients', description: 'Client logos (200x100 recommended)' }
];

export default function AdminPage() {
  const [selectedCategory, setSelectedCategory] = useState('gallery');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [dragActive, setDragActive] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/images?category=${selectedCategory}`);
      setImages(response.data);
    } catch (error) {
      console.error('Failed to fetch images:', error);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setMessage({ type: '', text: '' });
    
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', selectedCategory);
      
      try {
        await axios.post(`${BACKEND_URL}/api/images/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error('Upload failed:', error);
      }
    }
    
    setUploading(false);
    
    if (successCount > 0) {
      setMessage({ type: 'success', text: `${successCount} image(s) uploaded successfully!` });
      fetchImages();
    }
    if (failCount > 0) {
      setMessage({ type: 'error', text: `${failCount} image(s) failed to upload.` });
    }
    
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/images/${imageId}`);
      setMessage({ type: 'success', text: 'Image deleted successfully!' });
      fetchImages();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete image.' });
    }
    
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const copyToClipboard = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <div className="page-transition" data-testid="admin-page">
      <Navbar />
      <main className="pt-24 pb-16 min-h-screen bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl text-navy mb-2">Image Manager</h1>
            <p className="text-muted-foreground">Upload and manage images for your website</p>
          </div>

          {/* Message */}
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 flex items-center gap-3 ${
                message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
            </motion.div>
          )}

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 sticky top-24">
                <h2 className="font-semibold text-navy mb-4">Categories</h2>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-navy text-white'
                          : 'bg-muted text-navy hover:bg-navy/10'
                      }`}
                    >
                      <div className="font-medium">{cat.name}</div>
                      <div className={`text-xs mt-1 ${selectedCategory === cat.id ? 'text-white/70' : 'text-muted-foreground'}`}>
                        {cat.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Upload Area */}
              <div
                className={`bg-white p-8 mb-8 border-2 border-dashed transition-colors ${
                  dragActive ? 'border-gold bg-gold/5' : 'border-gray-200'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-gold' : 'text-muted-foreground'}`} />
                  <h3 className="font-semibold text-navy mb-2">
                    {uploading ? 'Uploading...' : 'Drop images here or click to upload'}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Supports: JPG, PNG, WebP, GIF (Max 10MB each)
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleUpload(Array.from(e.target.files))}
                    className="hidden"
                    id="file-upload"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`btn-primary inline-flex items-center gap-2 cursor-pointer ${uploading ? 'opacity-50' : ''}`}
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Select Files'}
                  </label>
                </div>
              </div>

              {/* Images Grid */}
              <div className="bg-white p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-semibold text-navy">
                    {categories.find(c => c.id === selectedCategory)?.name} ({images.length})
                  </h2>
                </div>

                {images.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Image className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No images in this category yet.</p>
                    <p className="text-sm">Upload some images to get started!</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {images.map((image) => (
                      <motion.div
                        key={image.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative group border border-gray-100 overflow-hidden"
                      >
                        <img
                          src={`${BACKEND_URL}${image.url}`}
                          alt={image.title || image.original_name}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute inset-0 bg-navy/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => copyToClipboard(image.url)}
                            className="p-2 bg-white text-navy hover:bg-gold transition-colors"
                            title="Copy URL"
                          >
                            {copiedUrl === image.url ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => handleDelete(image.id)}
                            className="p-2 bg-white text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-3">
                          <p className="text-sm text-navy font-medium truncate">{image.original_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(image.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="mt-8 bg-gold/10 p-6 border-l-4 border-gold">
                <h3 className="font-semibold text-navy mb-3">How to Use Uploaded Images</h3>
                <ol className="text-sm text-muted-foreground space-y-2">
                  <li>1. Upload images to the appropriate category</li>
                  <li>2. Click the <Copy className="w-4 h-4 inline" /> icon to copy the image URL</li>
                  <li>3. Use the URL in your website code or share with your developer</li>
                  <li>4. For hero slideshow, upload images sized 1920x1080 pixels</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
